import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VisitsReportScreenContent.module.scss';
import { DATE_FORMAT_DEFAUT, DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, DefaultAssetImage } from 'Config';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import { useHistory, useLocation } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Popover from 'common/components/popover/Popover';
import VisitsReportFiltersScreen, { Filters } from '../VisitsReportFiltersScreen';
import { FaInfoCircle } from 'react-icons/fa';
import Loading from 'common/services/Loading';
import { GeofenceActivitiesSearchCriteria } from 'api/geofenceActivities/models/GeofenceActivitiesSearchCriteria';
import { GeofenceActivitiesDto } from 'api/geofenceActivities/models/GeofenceActivitiesDto';
import GeofenceActivitiesService from 'api/geofenceActivities/GeofenceActivitiesService';
import HourglassIcon from 'assets/svg/hour-glass_white.svg';
import PointIcon from 'assets/svg/desktop_GPS_white.svg';
import UserIcon from 'assets/svg/desktop_default_photo.svg';
import CarIcon from 'assets/svg/car.svg';
import { GeofenceActivitiesConterDto } from 'api/geofenceActivities/models/GeofenceActivitiesConterDto';
import { convertToMinutes } from 'common/utils/GlobalFunctions';
import IconWithText from 'common/components/iconWithText/IconWithText';
import PlayIcon from 'assets/svg/play2.svg';
import FlagBWIcon from 'assets/svg/flag_bw.svg';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import InfoCard from 'common/components/infoCard/InfoCard';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import ReactTooltip from 'react-tooltip';
import dayjs from 'dayjs';
import moment from 'moment';
import colors from 'styles/export/colors.module.scss';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
    colors.counterBlue2,
];

export type VisitsReportScreenProps = {
    geofenceId?: string;
    onNavigate?: () => void
}

const VisitsReportScreenContent = ({ geofenceId, onNavigate }: VisitsReportScreenProps) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const location = useLocation();

    const FILTERS_CACHE_KEY = 'VISITSREPORTFILTER'.concat(geofenceId != undefined ? `-${geofenceId}` : '');

    const parseUrlFilters = (): Filters | null => {
        const params = new URLSearchParams(location.search);
        const dateFrom = params.get('dateFrom');
        const dateTo = params.get('dateTo');
        if (!dateFrom && !dateTo) return null;
        const next: Filters = {};
        if (dateFrom) next.entryDate = new Date(dateFrom);
        if (dateTo) next.exitDate = new Date(dateTo);
        return next;
    };

    const [criteria, setCriteria] = useState<GeofenceActivitiesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'entry_date',
        orderBy: 'desc'
    });
    const [geofenceActivities, setGeofenceActivities] = useState<GeofenceActivitiesDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [counters, setCounters] = useState<GeofenceActivitiesConterDto>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasReadCostCenterPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COST_CENTER_READ']);

    const isFirstLoad = useRef(true);
    const countersLoaded = useRef<boolean>(false);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);
    const previousCriteria = useRef<GeofenceActivitiesSearchCriteria | null>(null);
    const isLoadingData = useRef<boolean>(false);

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    // Order state for DataTable
    const [order, setOrder] = useState<OrderData<GeofenceActivitiesDto>>({
        field: 'entryDate',
        orderColumn: 'entry_date',
        isOrderAsc: false,
    });

    const getFieldFromOrderColumn = (oc: string): keyof GeofenceActivitiesDto => {
        const mapping: Record<string, keyof GeofenceActivitiesDto> = {
            'g.name': 'geofenceName',
            'registration_number': 'registration',
            'verification_driver_name': 'verificationDriverName',
            'duration_minutes': 'duration',
            'entry_date': 'entryDate',
            'exit_date': 'exitDate',
        };
        return mapping[oc] ?? 'entryDate';
    };

    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        if (isLoadingData.current) {
            return;
        }
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            let countersDB = null;
            if (shouldUpdateCounters) {
                countersDB = await GeofenceActivitiesService.getCounters({ ...criteria, geofenceId: geofenceId ?? criteria.geofenceId });
            }

            const page = await GeofenceActivitiesService.getList({ ...criteria, geofenceId: geofenceId ?? criteria.geofenceId });
            setTotalItems(page.totalItems);

            if (isLoadMore) {
                setGeofenceActivities(prev => [...prev, ...page.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setGeofenceActivities(page.items);
            }

            if (countersDB) {
                setCounters(countersDB);
                countersLoaded.current = true;
            }

            setTimeout(() => ReactTooltip.rebuild(), 0);

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(page.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1, geofenceId: geofenceId ?? criteria.geofenceId };
                        const prevResult = await GeofenceActivitiesService.getList(prevCriteria);
                        setGeofenceActivities(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.GEOFENCES, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1, geofenceId: geofenceId ?? criteria.geofenceId };
                        const nextResult = await GeofenceActivitiesService.getList(nextCriteria);
                        setGeofenceActivities(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.GEOFENCES, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.GEOFENCES, `Couldn't get geofences activities item list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || geofenceActivities.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, geofenceActivities.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: GeofenceActivitiesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                geofenceId: geofenceId ?? criteria.geofenceId,
            };
            const result = await GeofenceActivitiesService.getList(prevCriteria);

            setGeofenceActivities(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.GEOFENCES, `Couldn't get previous geofence activities page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, geofenceId, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [geofenceActivities, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Handler de ordenacao
    const handleOrderChange = (orderData: OrderData<GeofenceActivitiesDto>) => {
        setOrder(orderData);
        setGeofenceActivities([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const renderTableCell = (text?: string | number) => {
        return (
            <div>{text ?? '-'}</div>
        )
    }


    const tableColumns: DataTableColumn<GeofenceActivitiesDto>[] = useMemo(() => {
        const cols: DataTableColumn<GeofenceActivitiesDto>[] = [];

        cols.push(
            // Imagem
            {
                field: 'photoUrl' as keyof GeofenceActivitiesDto,
                name: t('common.image') as string,
                hideOrderButton: true,
                width: 80,
                hideOn: ['sm', 'md'],
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.contentImage}>
                        <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.photoUrl ? 'url(' + row?.photoUrl + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + ')' : 'url(' + DefaultAssetImage + ')' }} />
                    </div>
                ),
            },
            // Ativo (bold)
            {
                field: 'registration' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.vehicle') as string,
                orderColumn: 'registration_number',
                width: 150,
                renderCell: row => (
                    <div className={styles.columnPlate}>
                        {row.registration ?? t('visits_report.list.no_asset_defined')}
                        {!row.registration && row.code && <span style={{ marginLeft: '5px' }}><FaInfoCircle data-tip={t('visits_report.list.no_asset_defined_tooltip').replace('{0}', row.code)} /> <ReactTooltip /></span>}
                    </div>
                ),
            },
            // Marca
            {
                field: 'vehicleBrandName' as keyof GeofenceActivitiesDto,
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            // Modelo
            {
                field: 'vehicleModelName' as keyof GeofenceActivitiesDto,
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 140,
                hideOn: ['sm', 'md', 'lg'],
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            // Segmento
            {
                field: 'vehicleSegmentName' as keyof GeofenceActivitiesDto,
                name: t('vehicle.type') as string,
                width: 130,
                renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
            },
            // Agência
            {
                field: 'vehicleLocalNames' as keyof GeofenceActivitiesDto,
                name: t('vehicle.local') as string,
                hideOrderButton: true,
                width: 130,
                renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
            },
        );

        // Centro de custo (após Agência)
        if (hasReadCostCenterPolicy && !geofenceId) {
            cols.push({
                field: 'geofenceName' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.stopping_area') as string,
                orderColumn: 'g.name',
                width: 200,
                renderCell: row => renderTableCell(row.geofenceName),
            });
        }

        cols.push(
            // Condutor
            {
                field: 'verificationDriverName' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.driver') as string,
                orderColumn: 'verification_driver_name',
                width: 180,
                renderCell: row => renderTableCell(row.verificationDriverName),
            },
            // Duração
            {
                field: 'duration' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.duration') as string,
                orderColumn: 'duration_minutes',
                width: 120,
                hideOn: ['sm', 'md'],
                headerNote: `${t('common.unit')}: h`,
                renderCell: row => <>{row.duration ?? '-'} h</>,
            },
            // Data início
            {
                field: 'entryDate' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.visit_start_date') as string,
                orderColumn: 'entry_date',
                width: 180,
                hideOn: ['sm', 'md'],
                exportValue: row => row.entryDate ? moment(row.entryDate).format(DATE_TIME_FORMAT_DEFAULT) : '',
                renderCell: row => (
                    <IconWithText
                        icon={PlayIcon}
                        valueDate={row.entryDate}
                        valueHour={row.entryDate.toString()}
                        isDate={true}
                    />
                ),
            },
            // Data fim
            {
                field: 'exitDate' as keyof GeofenceActivitiesDto,
                name: t('visits_report.list.visit_end_date') as string,
                orderColumn: 'exit_date',
                width: 180,
                hideOn: ['sm', 'md'],
                exportValue: row => row.exitDate ? moment(row.exitDate).format(DATE_TIME_FORMAT_DEFAULT) : '',
                renderCell: row => (
                    <IconWithText
                        icon={FlagBWIcon}
                        valueDate={row.exitDate}
                        valueHour={row.exitDate.toString()}
                        isDate={true}
                    />
                ),
            },
        );

        return cols;
    }, [t, hasReadCostCenterPolicy, geofenceId]);

    const onRowClick = (row: GeofenceActivitiesDto, index: number, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        // Calculate the actual page of the clicked item
        const actualPage = calculatePageFromIndex(index);

        // Save scroll position with actual page and item id
        const newFilters = { ...filters };
        newFilters.page = actualPage;
        newFilters.scrollToItemId = `${row.id ?? ''}_${row.tripId ?? ''}`;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        if (onNavigate) {
            onNavigate();
        }

        if (event.ctrlKey) {
            window.open(`/vehicles/trip/details/${row.vehicleId}/gps/list/${row.entryDate.toString()}/${row.tripId}/${row.geofenceId}`, '_blank');
            return;
        }
        history.push(`/vehicles/trip/details/${row.vehicleId}/gps/list/${row.entryDate.toString()}/${row.tripId}/${row.geofenceId}`);
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(f);
        if (f.scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, scrollToItemId: _scrollToItemId, ...filtersWithoutScrollData } = f as Filters & { scrollToItemId?: string };
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    useEffect(() => {
        if (isFirstLoad.current === true) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && geofenceActivities.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged = prev &&
                prev.vehicleId === criteria.vehicleId &&
                prev.geofenceId === criteria.geofenceId &&
                prev.entryDate === criteria.entryDate &&
                prev.exitDate === criteria.exitDate &&
                prev.driverId === criteria.driverId &&
                prev.durationStart === criteria.durationStart &&
                prev.durationEnd === criteria.durationEnd &&
                JSON.stringify(prev.localIds) === JSON.stringify(criteria.localIds) &&
                prev.localOperator === criteria.localOperator &&
                prev.segmentId === criteria.segmentId;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = { ...criteria };
        }
    }, [criteria]);

    const loadFilters = () => {
        isFirstLoad.current = false;

        const urlFilters = parseUrlFilters();
        if (urlFilters) {
            // URL params override cached filters when navigating from dashboard widgets.
            // Strip the query so a refresh falls back to cached behaviour.
            history.replace(location.pathname);
            onChangeFilters(urlFilters, true);
            return;
        }

        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            // If returning to list with saved scroll position
            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    vehicleId: f.vehicleId,
                    registration: f.vehicleName,
                    geofenceId: f.geofenceId,
                    entryDate: f.entryDate,
                    exitDate: f.exitDate,
                    driverId: f.driverId,
                    durationStart: f.durationStartLabel ? convertToMinutes(f.durationStartLabel) : undefined,
                    durationEnd: f.durationEndLabel ? convertToMinutes(f.durationEndLabel) : undefined,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    segmentId: f.segmentId,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }

                return;
            }

            // Normal load without scroll restoration
            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            onChangeFilters(f, true);
        } else {
            onChangeFilters({}, true);
        }
    };

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        if (!isOnLoad) {
            setGeofenceActivities([]);
            resetScrollState();
            setTotalItems(0);
        }

        setCriteria(prev => ({
            ...prev,
            vehicleId: f.vehicleId,
            registration: f.vehicleName,
            geofenceId: f.geofenceId,
            entryDate: f.entryDate,
            exitDate: f.exitDate,
            driverId: f.driverId,
            durationStart: f.durationStartLabel ? convertToMinutes(f.durationStartLabel) : undefined,
            durationEnd: f.durationEndLabel ? convertToMinutes(f.durationEndLabel) : undefined,
            localIds: f.localIds,
            localOperator: f.localOperator,
            segmentId: f.segmentId,
            orderBy: f.orderBy || prev.orderBy || 'desc',
            orderColumn: f.orderColumn || prev.orderColumn || 'entry_date',
            page: 1,
        }));

        setOrder({
            field: getFieldFromOrderColumn(f.orderColumn || 'entry_date'),
            orderColumn: f.orderColumn || 'entry_date',
            isOrderAsc: (f.orderBy || 'desc') === 'asc',
        });

        setReRenderSearch(r => r + 1);
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.vehicleId) total++;
        if (f.entryDate || f.exitDate) total++;
        if (f.geofenceId) total++;
        if (f.driverId) total++;
        if (f.durationStartLabel || f.durationEndLabel) total++;
        if (f.segmentId) total++;
        if (f.localIds?.length) total++;
        setFiltersTotal(total);
    };

    const updateFilters = (items: { key: string; value: any }[]) => {
        const newFilters = JSON.parse(JSON.stringify(filters));
        items.forEach((item: { key: string; value: any }) => {
            newFilters[item.key] = item.value;
        });
        onChangeFilters(newFilters);
    };

    // Build filter badge items for FilterBadgesOverflow
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('visits_report.list.registration') as string,
                value: filters.vehicleName,
                onRemove: () => updateFilters([{ key: 'vehicleId', value: undefined }, { key: 'vehicleName', value: undefined }])
            });
        }

        if (hasReadCostCenterPolicy && !geofenceId && filters.geofenceName) {
            items.push({
                key: 'geofence',
                label: t('visits_report.list.stopping_area') as string,
                value: filters.geofenceName,
                onRemove: () => updateFilters([{ key: 'geofenceId', value: undefined }, { key: 'geofenceName', value: undefined }])
            });
        }

        if (filters.entryDate || filters.exitDate) {
            const start = filters.entryDate ? dayjs(filters.entryDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.exitDate ? dayjs(filters.exitDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'dates',
                label: t('visits_report.list.visit_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => updateFilters([{ key: 'entryDate', value: undefined }, { key: 'exitDate', value: undefined }])
            });
        }

        if (filters.driverName) {
            items.push({
                key: 'driver',
                label: t('car_verification.verification.driver') as string,
                value: filters.driverName,
                onRemove: () => updateFilters([{ key: 'driverId', value: undefined }, { key: 'driverName', value: undefined }])
            });
        }

        if (filters.durationStartLabel || filters.durationEndLabel) {
            let value = '';
            if (filters.durationStartLabel && filters.durationEndLabel) {
                value = `${t('common.between')} ${filters.durationStartLabel} h ${t('common.and')} ${filters.durationEndLabel} h`;
            } else if (filters.durationStartLabel) {
                value = `${t('common.above')} ${filters.durationStartLabel} h`;
            } else if (filters.durationEndLabel) {
                value = `${t('common.below')} ${filters.durationEndLabel} h`;
            }
            items.push({
                key: 'duration',
                label: t('common.duration') as string,
                value,
                onRemove: () => updateFilters([
                    { key: 'durationStart', value: undefined },
                    { key: 'durationStartLabel', value: undefined },
                    { key: 'durationEnd', value: undefined },
                    { key: 'durationEndLabel', value: undefined }
                ])
            });
        }

        if (filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => updateFilters([{ key: 'segmentId', value: undefined }, { key: 'segmentName', value: undefined }])
            });
        }

        if (filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => updateFilters([{ key: 'localIds', value: [] }, { key: 'localNames', value: [] }])
            });
        }

        return items;
    }, [filters, t, hasReadCostCenterPolicy, geofenceId]);

    const renderCounterBoxes = () => {
        let hours = 0;
        let minutes = 0;

        if (counters) {
            const hoursHelper = counters.minutes / 60;
            hours = Math.floor(hoursHelper);
            minutes = hours > 0 ? (Math.fround((hoursHelper - hours) * 60)) : counters.minutes;
        }

        return (
            <div className={compactPageLayoutStyles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('visits_report.list.duration_in_hours') as string}
                        value={`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} h`}
                        icon={HourglassIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                    />
                )}
                {hasReadCostCenterPolicy && !geofenceId && counters && (
                    <InfoCard
                        label={t('visits_report.list.number_stopping_area') as string}
                        value={counters.totalGeofences}
                        icon={PointIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                    />
                )}
                {(geofenceId || !hasReadCostCenterPolicy) && counters && (
                    <InfoCard
                        label={t('visits_report.list.number_drivers') as string}
                        value={counters.totalDrivers}
                        icon={UserIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('visits_report.list.number_vehicles') as string}
                        value={counters.totalVehicles}
                        icon={CarIcon}
                        backgroundColor={INFO_CARD_COLORS[2]}
                    />
                )}
            </div>
        );
    };

    const renderFiltersPopover = () => (
        <Popover
            contentContainerClassName={styles.filtersPopoverContainer}
            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
            positions={['bottom', 'left']}
            align={'end'}
            onClickOutside={() => updateTotalFilters(filters)}
            content={setIsPopoverOpen => (
                <VisitsReportFiltersScreen
                    filters={filters}
                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                    onChange={f => updateTotalFilters(f)}
                    hideGeofenceSelector={geofenceId !== undefined}
                />
            )}
        >
            {(isPopoverOpen, setIsPopoverOpen) => (
                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                    {filtersTotal > 0 && (
                        <div className={styles.counterList}>
                            <div className={styles.counterNumber}>{filtersTotal}</div>
                        </div>
                    )}
                </ScreenHeaderButton>
            )}
        </Popover>
    );

    const fetchAllForExport = useCallback(async () => {
        const result = await GeofenceActivitiesService.getList({
            ...criteria,
            geofenceId: geofenceId ?? criteria.geofenceId,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria, geofenceId]);

    const content = (
        <>
            {counters && (
                <div className={compactPageLayoutStyles.countersSection}>
                    {renderCounterBoxes()}
                </div>
            )}
            <div className={compactPageLayoutStyles.listTblWrapper}>
                {isFirstLoad.current === false && (
                    <DataTableWithSidebar
                        key={reRenderSearch}
                        cacheKey="VISITSREPORT_TABLE"
                        columns={tableColumns}
                        defaultHiddenColumns={['vehicleSegmentName', 'vehicleLocalNames']}
                        rows={geofenceActivities}
                        defaultOrder={order}
                        onOrderChange={handleOrderChange}
                        onRowClick={(row, index, event) => onRowClick(row, index, event)}
                        onRowId={row => `${row.id ?? ''}_${row.tripId ?? ''}`}
                        scrollToRowId={scrollToItemId}
                        infiniteScroll={{
                            isLoading: isLoadingMore,
                            hasMore: getHasMore(geofenceActivities.length, totalItems),
                            onLoadMore: getOnLoadMore(loadMore),
                            hasMorePrevious: getHasMorePrevious(),
                            isLoadingPrevious: isLoadingPrevious,
                            onLoadPrevious: getOnLoadPrevious(loadPrevious),
                        }}
                        exportRef={exportListingRef}
                        hideExportInSidebar={true}
                        exportExcel={{
                            fetchAll: fetchAllForExport,
                            fileName: t('visits_report.list.title') as string,
                        }}
                    />
                )}
            </div>
        </>
    );

    if (geofenceId) {
        return (
            <div className={styles.embeddedWrapper}>
                <div className={styles.embeddedHeader}>
                    <FilterBadgesOverflow items={filterItems} align="right" />
                    <div className={styles.headerButtons}>
                        {renderFiltersPopover()}
                        <ExportListingButton exportRef={exportListingRef} />
                    </div>
                </div>
                {content}
            </div>
        );
    }

    return (
        <CompactPageLayout
            title={t('visits_report.list.title') as string}
            contentAreaClassName={styles.contentArea}
            middleContent={<FilterBadgesOverflow items={filterItems} align="right" />}
            headerButtons={
                <>
                    {renderFiltersPopover()}
                    <ExportListingButton exportRef={exportListingRef} />
                </>
            }
        >
            {content}
        </CompactPageLayout>
    );
};

export default VisitsReportScreenContent;
